import { useCallback, useEffect, useRef, useState } from "react";
import type { LabResult } from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";

export type LabResultsState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; results: LabResult[] };

export interface UseLabResultsResult {
  state: LabResultsState;
  // Re-runs the fetch without resetting to "loading" — a save just
  // succeeded, so the PWA needs the new row without a full page reload
  // (there is no hard refresh available inside the installed app shell).
  refetch: () => void;
}

// Fetch on mount plus on demand via refetch — no poll. Shape otherwise
// copied from useHomeSurface.ts. There is no react-query in this app; do not
// add it here either.
export function useLabResults(): UseLabResultsResult {
  const api = useApi();
  const [state, setState] = useState<LabResultsState>({ status: "loading" });
  const cancelledRef = useRef(false);

  const fetchResults = useCallback(() => {
    api.labs
      .list()
      .then((results) => {
        if (!cancelledRef.current) {
          setState({ status: "success", results });
        }
      })
      .catch((err: unknown) => {
        if (!cancelledRef.current) {
          setState({ status: "error", error: err instanceof Error ? err.message : "Failed to load lab results" });
        }
      });
  }, [api]);

  useEffect(() => {
    cancelledRef.current = false;
    fetchResults();
    return () => {
      cancelledRef.current = true;
    };
  }, [fetchResults]);

  return { state, refetch: fetchResults };
}
